/**
 * Local mock data for the TestHub UI (workaround while the API can't reach the
 * database / Azure DevOps). Wired in through the data services only when
 * `environment.useMockData` is true (see environment.local.ts), so nothing here
 * ships to dev/live/prod. Delete this file and the `useMockData` branches once the
 * API runs locally.
 */
import { Release } from '../models/release';
import { Suite } from '../models/suite';
import { TeamMember } from '../models/team-member';
import { TestCase } from '../models/test-case';
import { TestCaseComment } from '../models/test-case-comment';
import { TestPlan } from '../models/test-plan';
import { WorkItem } from '../models/work-item';
import { mockReleases } from './mock-release-data';


function plan(o: Partial<TestPlan> & Pick<TestPlan, 'testPlanId' | 'testPlanName' | 'suiteId'>): TestPlan {
  return {
    testPlanAzureId: o.testPlanId + 40000,
    numberOfPassingTests: 0,
    numberOfFailingTests: 0,
    numberOfTests: 0,
    numberOfShowstoppers: 0,
    numberOfBlockers: 0,
    numberOfFunctional: 0,
    numberOfCosmetic: 0,
    status: 'NotStarted',
    assignedUserId: null,
    assignedUserName: null,
    isLive: true,
    ...o,
  };
}

/**
 * Plans across two suites, deliberately covering every row state the UI renders:
 * not-started (empty bar + "Start"), in-progress (partial bar + "Continue"),
 * completed (full bar + "Review"), and a failing plan with a showstopper. All are
 * left unassigned so the tester view shows a populated "Unassigned" list and an
 * empty "Assigned to me" — claim one to see it move (and the claim spinner).
 */
const mockSuites: Suite[] = [
  {
    suiteId: 1,
    suiteName: 'Patient Records',
    suiteAzureId: 1001,
    testPlanSummaries: [
      plan({
        testPlanId: 101,
        testPlanName: 'Clinical Summary',
        suiteId: 1,
        status: 'InProgress',
        numberOfTests: 7,
        numberOfPassingTests: 3,
        numberOfFailingTests: 0,
      }),
      plan({
        testPlanId: 102,
        testPlanName: 'Medication List',
        suiteId: 1,
        status: 'NotStarted',
        numberOfTests: 5,
      }),
      plan({
        testPlanId: 103,
        testPlanName: 'Lab Results Display',
        suiteId: 1,
        status: 'Failing',
        numberOfTests: 10,
        numberOfPassingTests: 4,
        numberOfFailingTests: 2,
        numberOfShowstoppers: 1,
      }),
    ],
  },
  {
    suiteId: 2,
    suiteName: 'Scheduling',
    suiteAzureId: 1002,
    testPlanSummaries: [
      plan({
        testPlanId: 201,
        testPlanName: 'Appointment Booking',
        suiteId: 2,
        status: 'Completed',
        numberOfTests: 8,
        numberOfPassingTests: 8,
      }),
      plan({
        testPlanId: 202,
        testPlanName: 'Reminders & Notifications',
        suiteId: 2,
        status: 'InProgress',
        numberOfTests: 6,
        numberOfPassingTests: 2,
        numberOfFailingTests: 1,
      }),
    ],
  },
];

/** Full release detail (with suites/plans) for the resolver-fed release pages. */
export function getMockReleaseById(releaseId: number): Release {
  const base =
    mockReleases.find((r) => r.releaseId === releaseId) ?? mockReleases[0];
  // Active releases get the full suite tree so every hero on the releases page
  // leads somewhere; closed ones are fine bare.
  const suites = base.isActive ? mockSuites : [];
  return { ...base, releaseId, suites };
}

const stepsHtml = `
<table>
  <thead>
    <tr><th>Step</th><th>Action</th><th>Expected Result</th></tr>
  </thead>
  <tbody>
    <tr><td>1</td><td>From your selected area (Physician Ambulatory or Physician Inpatient) open the Clinical Summary.</td><td>The Labs section is visible. Trends show for labs only when testing Inpatient.</td></tr>
    <tr><td>2</td><td>Check labs drawn today.</td><td>Today's labs are shown in <strong>bold</strong>. Only the time is shown in parentheses, e.g. (13:00). No trend indicators appear.</td></tr>
    <tr><td>3</td><td>Check lab results from the last 30 days (but not today).</td><td>Labs are shown in <strong>bold</strong> with the date in parentheses, e.g. (08/25/2025). No trend indicators.</td></tr>
    <tr><td>4</td><td>Check lab results older than 90 days.</td><td>These labs are <strong>not</strong> bolded under Recent Results.</td></tr>
    <tr><td>5</td><td>Verify favorite labs from the Data tab.</td><td>Favorites not already shown as recent results appear in regular text, in the custom order defined on the data tab.</td></tr>
  </tbody>
</table>`;

/** A single work item used for every test case in mock mode (steps render as a table). */
export function getMockWorkItem(testCaseId: number): WorkItem {
  return {
    testCaseAzureId: testCaseId,
    testCaseName: 'Verify Clinical Summary Lab Results Display for Ambulatory Users',
    description:
      'Ensures that when a patient has existing labs, the results shown in the Clinical Summary use the correct formatting and ordering.',
    steps: stepsHtml,
    formattedDescription: '',
  };
}

/** Test cases for a plan (drives the execute-screen stepper). */
export function getMockTestCases(testPlanId: number): TestCase[] {
  const names = [
    'Recent labs render in bold',
    'Time-only formatting for today',
    'Date formatting within 30 days',
    'Older labs are not bolded',
    'Favorite labs respect custom order',
  ];
  // A little variety so the stepper shows passed / failed / remaining nodes.
  const passing: (boolean | null)[] = [true, false, null, null, null];
  return names.map((name, i) => ({
    testCaseId: testPlanId * 100 + i + 1,
    testCaseName: name,
    testCaseAzureId: testPlanId * 100 + i + 1,
    testPlanId,
    isPassing: passing[i],
    isLive: true,
  }));
}

export const mockUsers: TeamMember[] = [
  { userId: 999, azureId: 'a-1', firstName: 'Marcus', lastName: 'Chen', isActive: true, isAdministrator: false },
  { userId: 998, azureId: 'a-2', firstName: 'Priya', lastName: 'Nair', isActive: true, isAdministrator: true },
];

/** Build a claimed/assigned plan for the optimistic assign/reassign mock responses. */
export function mockAssignedPlan(testPlanId: number, assignedUserId: number, assignedUserName: string): TestPlan {
  return plan({
    testPlanId,
    testPlanName: 'Assigned plan',
    suiteId: 1,
    assignedUserId,
    assignedUserName,
  });
}

/**
 * Comments on a test case (PBI 57768). The first case in each plan carries a short
 * thread; the rest start empty so the empty state is reachable. Timestamps are
 * relative to now so the thread reads "Yesterday" / "Today, …".
 */
export function getMockTestCaseComments(testCaseId: number): TestCaseComment[] {
  if (testCaseId % 100 !== 1) return [];
  const hoursAgo = (h: number) => new Date(Date.now() - h * 3600000).toISOString();
  return [
    {
      testCaseCommentId: testCaseId * 10 + 1,
      testCaseId,
      userId: 999,
      userName: 'Marcus Chen',
      userRole: 'Tester',
      body: 'Recent labs bold correctly, but the date in parentheses uses the ordering timezone.\nWorth a look before sign-off.',
      isDeleted: false,
      createdOnUtc: hoursAgo(26),
      modifiedOnUtc: null,
    },
    {
      testCaseCommentId: testCaseId * 10 + 2,
      testCaseId,
      userId: 998,
      userName: 'Priya Nair',
      userRole: 'Admin',
      body: 'Confirmed on build 2.14 — logging it against the release.',
      isDeleted: false,
      createdOnUtc: hoursAgo(2),
      modifiedOnUtc: null,
    },
  ];
}
